import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface RoomConfig {
  singleRooms: string;
  sharedRooms: string;
  communalRooms: string;
  communalBeds: string;
  startingNumber: string;
}

interface RoomConfigSectionProps {
  config: RoomConfig;
  onChange: (config: RoomConfig) => void;
}

export function RoomConfigSection({ config, onChange }: RoomConfigSectionProps) {
  const update = (key: keyof RoomConfig, value: string) => {
    onChange({ ...config, [key]: value });
  };

  const totalRooms = (parseInt(config.singleRooms) || 0) +
    (parseInt(config.sharedRooms) || 0) * 2 +
    (parseInt(config.communalRooms) || 0);

  return (
    <div className="border-t pt-4 space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold">Room Configuration</h4>
        <span className="text-xs text-muted-foreground">
          {totalRooms} rooms will be generated
        </span>
      </div>

      <div>
        <Label>Starting Room Number</Label>
        <Input
          type="number"
          value={config.startingNumber}
          onChange={(e) => update('startingNumber', e.target.value)}
          placeholder="101"
        />
        <p className="text-xs text-muted-foreground mt-1">Rooms will be numbered sequentially from here</p>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div>
          <Label className="text-xs">Single Rooms</Label>
          <Input
            type="number"
            min="0"
            value={config.singleRooms}
            onChange={(e) => update('singleRooms', e.target.value)}
            placeholder="0"
          />
          <p className="text-[10px] text-muted-foreground mt-0.5">1 bed each</p>
        </div>
        <div>
          <Label className="text-xs">Shared Rooms</Label>
          <Input
            type="number"
            min="0"
            value={config.sharedRooms}
            onChange={(e) => update('sharedRooms', e.target.value)}
            placeholder="0"
          />
          <p className="text-[10px] text-muted-foreground mt-0.5">2 beds (a/b)</p>
        </div>
        <div>
          <Label className="text-xs">Communal Rooms</Label>
          <Input
            type="number"
            min="0"
            value={config.communalRooms}
            onChange={(e) => update('communalRooms', e.target.value)}
            placeholder="0"
          />
          <p className="text-[10px] text-muted-foreground mt-0.5">N beds each</p>
        </div>
      </div>

      {(parseInt(config.communalRooms) || 0) > 0 && (
        <div>
          <Label>Beds per Communal Room</Label>
          <Input
            type="number"
            min="2"
            value={config.communalBeds}
            onChange={(e) => update('communalBeds', e.target.value)}
            placeholder="4"
          />
        </div>
      )}
    </div>
  );
}

export type { RoomConfig };
